import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { apiClient } from './api-client';

/**
 * System settings (spec §78).
 *
 * Every business rule the platform enforces — deposit minimums, MOQ defaults,
 * GST, quotation validity, KYC document requirements — is a row in this table.
 * The screen that edits them is the only place they can be changed, and the
 * server re-validates every value regardless of what this file allows.
 */

export type SettingType = 'STRING' | 'NUMBER' | 'BOOLEAN' | 'JSON';

export type SettingGroup =
  | 'COMPANY'
  | 'ORDER'
  | 'PAYMENT'
  | 'SHIPPING'
  | 'SECURITY_DEPOSIT'
  | 'VERIFICATION'
  | 'CATALOG'
  | 'INVENTORY'
  | 'ENGAGEMENT'
  | 'QUOTATION'
  | 'NOTIFICATION'
  | 'AI'
  | 'SEO'
  | 'LEGAL';

export interface AdminSetting {
  key: string;
  /** Already coerced to `type` by the server. */
  value: unknown;
  type: SettingType;
  group: SettingGroup;
  label: string;
  description: string | null;
  /** Readable by the storefront without a session. */
  isPublic: boolean;
  /** Only a super admin may change it. */
  isLocked: boolean;
  updatedAt: string;
}

const keys = {
  all: ['admin', 'settings'] as const,
};

export function useSettings() {
  return useQuery({
    queryKey: keys.all,
    queryFn: () => apiClient.get<AdminSetting[]>('/admin/settings'),
  });
}

/**
 * Save a batch of edits.
 *
 * The server applies them all or none, and answers with the full settings list,
 * so the cache is seeded from the response rather than refetched — a refetch
 * would race the Redis invalidation that just happened on the write.
 */
export function useSaveSettings() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (updates: Array<{ key: string; value: unknown }>) =>
      apiClient.patch<AdminSetting[]>('/admin/settings', { updates }),
    onSuccess: (settings) => {
      queryClient.setQueryData(keys.all, settings);
    },
  });
}

/** Group order and labels for the settings screen's sections. */
export const SETTING_GROUP_LABEL: Record<SettingGroup, string> = {
  COMPANY: 'Company',
  SECURITY_DEPOSIT: 'Security deposit',
  VERIFICATION: 'Business verification',
  ORDER: 'Orders & tax',
  SHIPPING: 'Shipping',
  QUOTATION: 'Quotations',
  CATALOG: 'Catalogue',
  INVENTORY: 'Inventory',
  ENGAGEMENT: 'Engagement',
  AI: 'AI assistant',
  PAYMENT: 'Payments',
  NOTIFICATION: 'Notifications',
  SEO: 'SEO',
  LEGAL: 'Legal',
};

export const SETTING_GROUP_ORDER: SettingGroup[] = [
  'COMPANY',
  'SECURITY_DEPOSIT',
  'VERIFICATION',
  'ORDER',
  'SHIPPING',
  'QUOTATION',
  'CATALOG',
  'INVENTORY',
  'ENGAGEMENT',
  'AI',
  'PAYMENT',
  'NOTIFICATION',
  'SEO',
  'LEGAL',
];
